// Pagina ordine /ordini/:ref (redesign 2026-10-23, Davide: «modifiche più
// profonde… soprattutto per funzionalità operativa… voglio poter fare TUTTO»).
//
// Dall'alto: testata ferma → promemoria sostituzione → «Prossimo passo» e il
// percorso in cinque tappe, ognuna coi suoi tasti (PercorsoOrdine) → sotto,
// due colonne: a sinistra la merce (prodotti, spedizione, resi), a destra chi
// e quanto (cliente, indirizzi, conti, date, attività).
//
// Qui vivono gli hook di TUTTE le azioni: la fascia «Prossimo passo» e le
// tappe chiamano gli stessi handler, cosi' lo stesso tasto fa sempre la
// stessa cosa ovunque compaia.
//
// ``min-w-0`` sulla colonna larga è obbligatorio: il minimo implicito di
// ``1fr`` è il CONTENUTO, e senza quello la tabella prodotti sfonda la finestra.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useReturnDialog } from "@/features/resi/useReturnDialog";
import { prettifyApiError } from "@/shared/api";
import { formatDateTime, formatMoney } from "@/shared/format";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { AREA_CLS } from "@/shared/ui/Collapsible";
import { InvoiceInfo } from "@/shared/ui/InvoiceInfo";
import { fetchPrenotazioni } from "@/features/ritiri/api";
import { useOrderDetail } from "./api";
import { canaleDirettoDiUnOrdine, confermaCanale, messaggioConferma } from "./confermaCanaleApi";
import { type ContestoPercorso, contestoDi } from "./percorso";
import { ritiroDelRicambio } from "./sostituzione";
import { ActivitySection } from "./components/detail/ActivitySection";
import { AddressesSection } from "./components/detail/AddressesSection";
import { ContiSection } from "./components/detail/ContiSection";
import { CustomerSection } from "./components/detail/CustomerSection";
import { OrderHeader } from "./components/detail/OrderHeader";
import { CostoNonNotoBox } from "./components/CostoNonNotoBox";
import { ProductsSection } from "./components/detail/ProductsSection";
import { PromemoriaSostituzione } from "./components/detail/PromemoriaSostituzione";
import { ReplacementsSection } from "./components/detail/ReplacementsSection";
import { ShipmentsSection } from "./components/detail/ShipmentsSection";
import { ShippingSection } from "./components/detail/ShippingSection";
import { VenditaExtraDialog } from "./components/detail/VenditaExtraDialog";
import { PercorsoOrdine } from "./components/detail/PercorsoOrdine";
import { EMPTY_DRAFT, opzioniLdv } from "./components/detail/azioni";
import type { ShippingDraft } from "./components/ShippingEditor";
import { LABEL, PANEL, TITLE } from "./components/detail/stile";
import type { Order } from "./types";
import { useLabelActions } from "./useLabelActions";
import { useOrderMargins } from "./useOrderMargins";
import { useOrdinaFornitore } from "./useOrdinaFornitore";
import { usePickupMailAction } from "./usePickupMailAction";
import { useStatoOrdine } from "./useStatoOrdine";

type Ritiro = NonNullable<ContestoPercorso["ritiro"]>;

/** Il ritiro SDA prenotato da OM che contiene questo ordine, se c'è. Serve a
 *  dire «ritiro del 24/10» nella tappa Ritiro e a sapere se il corriere l'ha
 *  già preso (``partiti``). Best-effort: senza risposta la tappa lo tace. */
function useRitiroDellOrdine(ref: string | undefined): Ritiro | null {
  const [ritiro, setRitiro] = useState<Ritiro | null>(null);
  useEffect(() => {
    if (!ref) return undefined;
    let cancelled = false;
    fetchPrenotazioni({ tipo: "fornitore" })
      .then((lista) => {
        if (cancelled || !Array.isArray(lista)) return;
        const p = lista.find(
          (x) =>
            x.ordini?.includes(ref) &&
            !x.lasciati?.includes(ref) &&
            x.stato !== "annullato" &&
            x.stato !== "rifiutato",
        );
        setRitiro(
          p
            ? { data: p.data_ritiro, codice: p.codice, partito: p.partiti?.includes(ref) ?? false }
            : null,
        );
      })
      .catch(() => {
        // ponytail: senza ritiri la tappa dice solo cosa aspetta — nessun toast.
      });
    return () => {
      cancelled = true;
    };
  }, [ref]);
  return ritiro;
}

/** Testo da una chiave del raw Poleepo. Il raw è JSONB senza contratto: quello
 *  che non è stringa/numero non si mostra — meglio un campo assente che
 *  «[object Object]» a video. */
function rawText(raw: Record<string, unknown> | null | undefined, key: string): string {
  const v = raw?.[key];
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

interface Campo {
  k: string;
  v: string;
  mono?: boolean;
}

/** Solo i campi con un valore: su un ordine fresco 5 su 8 sono vuoti, e una
 *  colonna di «—» è rumore che si legge ogni volta e non dice mai niente. */
function pieni(campi: Campo[]): Campo[] {
  return campi.filter((c) => c.v !== "");
}

/** «Quando»: le date dell'ordine, in verticale nella colonna 3. Stava in
 *  testata come striscia a 8 colonne — otto campi che si guardano una volta,
 *  larghi quanto la pagina, sopra il lavoro vero.
 *  Chiavi VERIFICATE sul raw di un ordine reale (406646, letto dal DB il
 *  16/07): il raw non ha contratto, si leggono quelle che esistono davvero. */
function SezioneQuando({ order }: { order: Order }) {
  const raw = order.raw;
  const discount = raw?.discount;
  const date = pieni([
    { k: "Ordine", v: formatDateTime(order.createdAtPoleepo ?? null) },
    { k: "Fattura ricevuta", v: formatDateTime(rawText(raw, "fattura_ricevuta_il") || null) },
    {
      k: ritiroDelRicambio(order) ? "Ricambio pronto" : "Merce pronta",
      v: formatDateTime(rawText(raw, "merce_pronta_il") || null),
    },
    { k: "Confermato", v: formatDateTime(rawText(raw, "confermato_il") || null) },
    // updatedAtPoleepo, NON raw.update_date: il backend normalizza il fuso
    // (Poleepo marca `Z` l'ora locale italiana → dal raw sarebbe +2h).
    { k: "Ultimo update", v: formatDateTime(order.updatedAtPoleepo ?? null) },
  ]);
  // Coda grigia: gli identificativi e il come si paga/spedisce. ``cod`` false è
  // la norma (regola: Cosma mai contrassegno), si dice solo quando è vero.
  const coda = pieni([
    { k: "Poleepo id", v: rawText(raw, "id"), mono: true },
    { k: "Pagamento", v: rawText(raw, "payment_method") },
    { k: "Contrassegno", v: raw?.cod === true ? "contrassegno" : "" },
    { k: "Spedizione", v: rawText(raw, "shipping_method") },
    // Sconto 0 = nessuno sconto: la riga direbbe «€ 0,00» su quasi tutti.
    {
      k: "Sconto",
      v: typeof discount === "number" && discount > 0 ? formatMoney(discount, order.currency) : "",
    },
  ]);
  if (date.length === 0 && coda.length === 0) return null;

  return (
    <section className={`${PANEL} ${AREA_CLS.tempo}`}>
      <h3 className={`mb-2 ${TITLE}`}>Quando</h3>
      {date.map((c) => (
        <div
          key={c.k}
          className="flex items-baseline justify-between gap-2 border-t border-line py-1.5 text-[13px]"
        >
          <span className={LABEL}>{c.k}</span>
          <span className="font-semibold tabular-nums text-ink">{c.v}</span>
        </div>
      ))}
      {coda.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 border-t border-line pt-2 text-[12px] text-ink">
          {coda.map((c) => (
            // ``title``: l'etichetta per esteso senza spendere una riga a testa.
            <span key={c.k} title={c.k} className={c.mono ? "font-mono" : ""}>
              {c.v}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

export function OrderDetailPage() {
  const { ref } = useParams<{ ref: string }>();
  const { order, isLoading, error, reload } = useOrderDetail(ref);
  const {
    openFor,
    dialog: returnDialog,
    busyOrder: returnBusyOrder,
    openError: returnOpenError,
  } = useReturnDialog(reload);
  // Hooks incondizionati: chiamato PRIMA degli early-return, con array vuoto
  // finché l'ordine non è ancora caricato (useOrderDetail può dare null).
  const {
    rows: marginRows,
    paramsSyncedAt,
    refresh: rileggiMargine,
  } = useOrderMargins(order ? [order.id] : [], Boolean(order));
  // «Genera LDV» (Piano 3 Task 2): stesso hook di OrdiniPage, riusato qui.
  // ``reload`` e' gia' stabile (useCallback, deps []) — nessun wrapper
  // aggiuntivo serve per non invalidare la memoizzazione interna dell'hook.
  // Sink locali (toast pagina) al posto degli ``actionMessage``/``actionError``
  // di OrdiniPage. isMockMode: false — la pagina ordine e' sempre live.
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const labelActions = useLabelActions({
    onRefresh: reload,
    onMessage: setActionMessage,
    onError: setActionError,
    isMockMode: false,
  });
  // «Invia LDV» (bug #4): stesso hook della lista. La prima stesura rifaceva
  // qui la macchina a stati e saltava il gate ``mail_backend`` → su smtp (il
  // backend REALE del web) la mail al fornitore partiva al click, senza che
  // Davide la rileggesse. Ora la regola sta in un posto solo.
  const pickupMail = usePickupMailAction({
    onRefresh: reload,
    onMessage: setActionMessage,
    onError: setActionError,
    isMockMode: false,
  });
  // «Ordina al fornitore» e «Spedito / cambia stato»: gli stessi flussi della
  // lista, con un ordine solo. Prima dalla pagina ordine non si potevano fare.
  const ordina = useOrdinaFornitore({
    onMessage: setActionMessage,
    onError: setActionError,
    onRefresh: reload,
  });
  const stato = useStatoOrdine(order, {
    onMessage: setActionMessage,
    onError: setActionError,
    onRefresh: reload,
  });
  const ritiro = useRitiroDellOrdine(order?.id);
  // Il draft dell'editor colli: condiviso fra ShippingSection (dove si scrive)
  // e la tappa Etichetta (dove si preme «Genera LDV»).
  const draftRef = useRef<ShippingDraft>(EMPTY_DRAFT);
  // Lettura dal vivo del pannello tracking: «Consegnata» chiude la tappa
  // Spedito anche prima che il job orario scriva ``lastTrackingEvent``.
  const [consegnataDalVivo, setConsegnataDalVivo] = useState(false);
  const segnaConsegnata = useCallback(() => setConsegnataDalVivo(true), []);
  // «Conferma al marketplace»: chiude l'ordine sul canale di origine e gli
  // passa il tracking senza passare da Poleepo. Best-effort per costruzione —
  // il pacco e' gia' partito — quindi l'esito NEGATIVO va mostrato, non
  // inghiottito: e' un lavoro che resta da fare a mano.
  const [confermaBusy, setConfermaBusy] = useState(false);
  const [venditaExtra, setVenditaExtra] = useState(false);
  const handleConfermaCanale = useCallback(
    async (target: Order) => {
      const canale = canaleDirettoDiUnOrdine(target);
      if (!canale) return;
      setConfermaBusy(true);
      setActionError(null);
      setActionMessage(null);
      try {
        const { tono, testo } = messaggioConferma(await confermaCanale(target.id), canale);
        if (tono === "ok") setActionMessage(testo);
        else setActionError(testo);
      } catch (err) {
        setActionError(prettifyApiError(err));
      } finally {
        setConfermaBusy(false);
      }
      reload();
    },
    [reload],
  );

  if (isLoading && !order) {
    return (
      <div role="status" aria-live="polite" className="p-8 text-[13.5px] text-ink">
        Caricamento ordine…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <ErrorBanner message={error} />
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={reload}
            className="rounded-sm border border-line bg-card px-3 py-1.5 text-[12.5px] font-medium text-ink hover:border-accent"
          >
            Riprova
          </button>
          <Link
            to="/ordini"
            className="rounded-sm border border-line bg-card px-3 py-1.5 text-[12.5px] font-medium text-ink hover:border-accent"
          >
            ‹ Torna agli ordini
          </Link>
        </div>
      </div>
    );
  }
  if (!order) return null;

  const marginRow = marginRows.get(order.id) ?? null;
  const apriSostituzione = () => void openFor(order.id, "sostituzione");
  const flussi = ordina.flussi([order]);
  const ctx = contestoDi(order, { flussi, ritiro, consegnatoDalVivo: consegnataDalVivo });

  return (
    // min-h-0 + flex-1 + overflow-y-auto: la colonna contenuto di AppShell e'
    // overflow-hidden (bottom nav mobile) → ogni pagina scrolla da se'
    // (Davide 2026-07-14: "la pagina ordine non scorre").
    <div className="min-h-0 flex-1 overflow-y-auto bg-paper scroll-paper">
      <OrderHeader order={order} />

      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4 px-5 pb-6 pt-4 max-md:px-3 max-md:pb-3 max-md:pt-3">
        {/* Promemoria fuori da ogni colonna e da ogni piega. ``empty:hidden``:
            senza sostituzione il wrapper non occupa nulla. */}
        <div className="empty:hidden">
          <PromemoriaSostituzione
            order={order}
            onCreaEtichetta={apriSostituzione}
            onChanged={reload}
            onMessage={setActionMessage}
            onError={setActionError}
          />
        </div>

        <PercorsoOrdine
          order={order}
          ctx={ctx}
          flussi={flussi}
          ordinaBusy={ordina.busy || ordina.dialogBusy}
          onGeneraLdv={(redo) =>
            void labelActions.generateLabel(order, opzioniLdv(draftRef.current, redo))
          }
          labelBusy={labelActions.labelBusyIds.has(order.id)}
          onInviaLdv={pickupMail.sendPickupMail}
          pickupMailBusy={pickupMail.pickupMailBusyIds.has(order.id)}
          onConfermaCanale={() => void handleConfermaCanale(order)}
          confermaBusy={confermaBusy}
          onStato={stato.chiedi}
          statoBusy={stato.busy}
          onChanged={reload}
          onMessage={setActionMessage}
        />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* La merce: cosa, come parte, cosa torna indietro. */}
          <div data-testid="detail-mid" className="om-rise min-w-0 [animation-delay:120ms]">
            <CostoNonNotoBox
              riferimento={order.id}
              onSaved={() => void rileggiMargine?.(order.id)}
            />
            <ProductsSection order={order} row={marginRow} />
            <ShippingSection
              order={order}
              onChanged={reload}
              onGenerateLabel={labelActions.generateLabel}
              labelBusy={labelActions.labelBusyIds.has(order.id)}
              draftRef={draftRef}
              onConsegnata={segnaConsegnata}
            />
            {/* Solo per gli ordini con 2+ spedizioni: sotto quella soglia
                ShipmentsSection non si renderizza e non chiama Poleepo. */}
            <ShipmentsSection order={order} />
            <ReplacementsSection
              order={order}
              onChanged={reload}
              onCreateReturn={(ref, kind) => void openFor(ref, kind)}
              returnBusy={returnBusyOrder === order.id}
            />
          </div>

          {/* Chi e quanto: il cliente sopra, poi i soldi e le date. */}
          <div className="om-rise min-w-0 [animation-delay:160ms]">
            {/* Wrapper suo: il nome cliente compare anche in AddressesSection
                (destinatario), e i test hanno bisogno di uno scope stretto. */}
            <div data-testid="detail-left">
              <div data-testid="detail-customer">
                <CustomerSection
                  order={order}
                  onAddReplacement={apriSostituzione}
                  onAddressSaved={reload}
                />
              </div>
              <AddressesSection order={order} />
            </div>
            <div data-testid="detail-rail">
              <ContiSection order={order} row={marginRow} syncedAt={paramsSyncedAt} />
              <InvoiceInfo
                invoice={order.invoice}
                className={AREA_CLS.soldi}
                azione={
                  <button
                    type="button"
                    onClick={() => setVenditaExtra(true)}
                    className="rounded-md border border-line bg-card px-3 py-1.5 text-[12px] font-medium text-ink hover:border-ink-4"
                  >
                    Vendita extra
                  </button>
                }
              />
              <SezioneQuando order={order} />
              <ActivitySection order={order} />
            </div>
          </div>
        </div>
      </div>

      {returnDialog}
      {venditaExtra && order && (
        <VenditaExtraDialog orderRef={order.id} onClose={() => setVenditaExtra(false)} />
      )}
      {labelActions.dialogs}
      {pickupMail.dialog}
      {ordina.dialogs}
      {stato.dialog}
      {/* Toast reso e toast azioni (LDV, mail di ritiro, sostituzione tolta)
          sono SLOT INDIPENDENTI: prima ``returnOpenError`` gatava anche il
          feedback LDV (successo soppresso, errore LDV sostituito da un
          errore-reso ormai stantio — ``useReturnDialog.openError`` resta
          valorizzato finché non si riapre/riinvia il dialog reso, ANCHE dopo
          averlo chiuso). Impilati in colonna nello stesso angolo cosi' non si
          sovrappongono se capitano insieme. */}
      {/* Telefono: sopra la barra in basso (64px + gesti), a tutta riga. */}
      <div className="fixed bottom-5 left-1/2 z-50 flex max-w-sm -translate-x-1/2 flex-col items-center gap-2 max-md:bottom-[calc(env(safe-area-inset-bottom)+80px)] max-md:left-3 max-md:right-3 max-md:max-w-none max-md:translate-x-0">
        {/* Stessa ricetta toast errore del resto dell'app (RailLogoutButton). */}
        <ErrorBanner message={returnOpenError} className="shadow-popover" />
        <ErrorBanner message={actionError} className="shadow-popover" />
        {actionMessage && !actionError && (
          <div
            role="status"
            className="rounded-md border border-green/20 bg-green-soft px-3.5 py-2 text-[12.5px] text-ink shadow-popover"
          >
            {actionMessage}
          </div>
        )}
      </div>
    </div>
  );
}
